import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Moon, Apple, TrendingDown, Fish, Brain, CheckCircle2, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ProtocolTracker } from './ProtocolTracker';
import { ProtocolReminderSettings } from './ProtocolReminderSettings';

const iconMap = {
  sleep: Moon,
  diet: Apple,
  stress: Brain,
  smoking: TrendingDown,
  activity: TrendingDown,
  alcohol: TrendingDown,
  hydration: Fish,
  social: Fish
};

export function ProtocolsList() {
  const navigate = useNavigate();

  const { data: protocols, isLoading } = useQuery({
    queryKey: ['protocols'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('protocols')
        .select('*')
        .order('factor_key');
      
      if (error) throw error;
      return data;
    }
  });

  const { data: userProgress } = useQuery({
    queryKey: ['all-protocol-progress'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return {};

      const { data, error } = await supabase
        .from('protocol_progress')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      // Group by protocol_id
      return (data || []).reduce((acc, p) => {
        if (!acc[p.protocol_id]) acc[p.protocol_id] = [];
        acc[p.protocol_id].push(p);
        return acc;
      }, {} as Record<string, any[]>);
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Active Protocol Tracker */}
      <ProtocolTracker />

      {/* Reminder Settings */}
      <ProtocolReminderSettings />

      {/* All Protocols */}
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-2">All Protocols</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Small, steady changes beat perfect plans. Choose a protocol that addresses your top contributor
            and commit to one week at a time.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {protocols?.map(protocol => {
            const Icon = iconMap[protocol.factor_key as keyof typeof iconMap] || TrendingDown;
            const expectedRange = `${protocol.expected_il_delta_min} to ${protocol.expected_il_delta_max}`;
            const progress = userProgress?.[protocol.id] || [];
            const completedDays = progress.filter((p: any) => p.completed).length;
            const hasStarted = progress.length > 0;
            const isComplete = completedDays >= protocol.duration_days;
            const progressPercent = hasStarted ? (completedDays / protocol.duration_days) * 100 : 0;

            return (
              <Card key={protocol.id} className={`p-6 hover:shadow-lg transition-shadow ${isComplete ? 'border-green-500/30 bg-green-500/5' : ''}`}>
                <div className="flex items-start gap-3 mb-4">
                  <div className={`p-2 rounded-lg ${isComplete ? 'bg-green-500/20' : 'bg-primary/10'}`}>
                    {isComplete ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <Icon className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">{protocol.name}</h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {protocol.duration_days} days
                      </Badge>
                      {isComplete && (
                        <Badge className="bg-green-500 text-xs">Complete</Badge>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  {protocol.description}
                </p>

                {hasStarted && !isComplete && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{completedDays}/{protocol.duration_days} days</span>
                    </div>
                    <Progress value={progressPercent} className="h-2" />
                  </div>
                )}

                <div className="bg-muted/50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-muted-foreground mb-1">Expected IL impact</p>
                  <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                    {expectedRange} points
                  </p>
                </div>

                <Button
                  onClick={() => navigate(`/longevity/protocol/${protocol.id}`)}
                  className="w-full"
                  variant={hasStarted && !isComplete ? 'default' : isComplete ? 'outline' : 'default'}
                >
                  {isComplete ? (
                    'View Details'
                  ) : hasStarted ? (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Continue
                    </>
                  ) : (
                    'Start Protocol'
                  )}
                </Button>
              </Card>
            );
          })}
        </div>

        <Card className="p-6 bg-primary/5 border-primary/20">
          <h3 className="font-semibold mb-2">Consistency is a lever</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Same lights-out time = calmer nervous system. Cook once, eat twice = fewer UPFs without more effort.
            Small habits compound over time.
          </p>
          <Button variant="outline" onClick={() => navigate('/therapy')}>
            Get Personalised Guidance
          </Button>
        </Card>
      </div>
    </div>
  );
}
